"use client";

import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedSubscribeButtonProps {
  subscribeStatus?: boolean;
  initialText: React.ReactElement | string;
  changeText: React.ReactElement | string;
  className?: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
}

export const AnimatedSubscribeButton: React.FC<AnimatedSubscribeButtonProps> = ({
  subscribeStatus = false,
  initialText,
  changeText,
  className,
  disabled,
  onClick,
  type = "button"
}) => {
  const [isSubscribed, setIsSubscribed] = useState<boolean>(subscribeStatus);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setIsSubscribed(!isSubscribed);
      onClick?.(e);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isSubscribed ? (
        <motion.button
          className={cn(
            "relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-violet-600/20 p-3 outline outline-1 outline-violet-500/50 text-violet-200",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          onClick={handleClick}
          disabled={disabled}
          type={type}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.span
            key="action"
            className="relative block h-full w-full font-semibold"
            initial={{ y: -50 }}
            animate={{ y: 0 }}
          >
            {changeText}
          </motion.span>
        </motion.button>
      ) : (
        <motion.button
          className={cn(
            "relative flex w-full cursor-pointer items-center justify-center rounded-lg border-none p-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold shadow-lg shadow-violet-500/25 transition-all duration-300",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          onClick={handleClick}
          disabled={disabled}
          type={type}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.span
            key="reaction"
            className="relative block font-semibold"
            initial={{ x: 0 }}
            exit={{ x: 50, transition: { duration: 0.1 } }}
          >
            {initialText}
          </motion.span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};